import React, { useState, useRef } from "react";
import _ from "lodash";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  BellRingOutlined,
  MinusCircleOutlined,
  AddCircleOutlined,
  CaretDownFilled,
} from "@toddle-edu/ds-icons";
import {
  SelectDropdown,
  IconButton,
  ChecklistDropdown,
  ButtonDisclosure,
  Button,
  TimePicker,
} from "@toddle-edu/ds-web";
import classes from "./Reminder.scss";
import EventRow from "../EventRow/EventRow";
import CustomModal from "./CustomModal";
import {
  mapStateToProps,
  mapActionCreators,
  MAX_REMINDER_COUNT,
} from "../../modules/Utils";
import { connect } from "react-redux";
import { useReminder, useReminderList } from "../../hooks";
import { useI18n } from "Hooks";
import classNames from "classnames";

/**
 * Ensures the referenced element is scrolled into view if not visible in viewport
 */
const useScrollIntoViewIfNeeded = () => {
  const ref = useRef(null);

  /**
   * Call this function when the target element is clicked
   */
  const scrollIntoViewOnClick = () => {
    const timerId = setTimeout(() => {
      clearTimeout(timerId);
      if (!ref.current) return;
      const element = ref.current;
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 10);
  };

  return [ref, scrollIntoViewOnClick];
};

/**
 * Comprehensive Reminder component with dropdown and modal functionality
 * Based on Figma designs for reminder selection and notifications
 */
const Reminder = ({
  initialScheduleTime = {},
  onRemindersChange,
  onRemindersChangeMultiple,
  onCustomReminderAdd,
  triggerTimeOffset,
  testId = "reminder-component",
  sentViaState = [],
  preferenceId = null, // Optional ID for tracking preferences
  existingReminderTimes: propExistingReminderTimes = [], // Array of already selected schedule times
  existingRemindersData = [], // Array of existing reminders with full data for duplicate detection
  serialNo,
  isAllDayEvent = false, // All-day event flag
  setToastMsg,
}) => {
  const triggerToastMsg = message => {
    setToastMsg(message);
  };
  const {
    selectedReminderTime,
    isCustomModalOpen,
    notificationPreferences,
    PRESET_SEND_VIA,
    handleReminderChange,
    handleNotificationToggle,
    handleReminderTimeChangeWithDuplicateCheck,
    allDayEventReminderTime,
    setIsCustomModalOpen,
    t,
    reminderScheduleOptions,
    onCustomValueChange,
    existingReminderTimes,
  } = useReminder({
    initialScheduleTime,
    onRemindersChange,
    onRemindersChangeMultiple,
    onCustomReminderAdd,
    triggerTimeOffset,
    sentViaState,
    existingReminderTimes: propExistingReminderTimes,
    existingRemindersData,
    isAllDayEvent, // Use the prop passed from parent
    triggerToastMsg,
  });

  const onChangeReminder = scheduleTime => {
    handleReminderChange(scheduleTime, preferenceId, "reminder");
  };
  const onChangeNotification = value => {
    handleNotificationToggle(value, preferenceId);
  };
  const onChangeReminderTime = value => {
    handleReminderTimeChangeWithDuplicateCheck(value, preferenceId);
  };
  const closeCustomModal = () => {
    setIsCustomModalOpen(false);
  };
  return (
    <div className={classes.reminderWrapper}>
      {/* Main reminder time selection */}
      <CustomModal
        isOpen={isCustomModalOpen}
        onClose={closeCustomModal}
        onCustomValueChange={onCustomValueChange}
        existingReminderTimes={existingReminderTimes}
        isAllDayEvent={isAllDayEvent}
      />
      <div className={classNames(classes.serialNo, "text-body")}>
        {serialNo}.
      </div>
      <div className={classes.reminderSection}>
        <SelectDropdown
          name="reminderTime"
          placeholder={t("calendar:select_reminder_time")}
          options={reminderScheduleOptions}
          value={selectedReminderTime}
          onChange={onChangeReminder}
          isMulti={false}
          isClearable={false}
          size="medium"
          testId={`${testId}-reminderTime`}
          dsVersion="2.0"
        />
      </div>
      <div className={classes.notificationSection}>
        <ChecklistDropdown
          label={t("calendar:via_email_inapp_push")}
          value={notificationPreferences || []}
          onChange={onChangeNotification}
          allOptionLabel={t("journal:all_folders")}
          isSearchEnabled={false}
          showSelectedOptionsOnTop={false}
          options={PRESET_SEND_VIA}
          dsVersion={"2.0"}
          showAllOption={false}
        >
          <ButtonDisclosure
            variant="neutral"
            type="outlined"
            className={classes.dropdownButton}
          >
            <span className={classNames(classes.buttonLabel, "text-body")}>
              {_.chain(PRESET_SEND_VIA)
                .filter(sendViaOption => {
                  return _.includes(
                    notificationPreferences,
                    sendViaOption?.value
                  );
                })
                .map("label")
                .join(", ")
                .capitalize()
                .value() || t("calendar:select_via_option")}
            </span>
          </ButtonDisclosure>
        </ChecklistDropdown>
      </div>
      {isAllDayEvent ? (
        <div className={classes.timePickerClass}>
          <TimePicker
            placeholder={t("common:start_time")}
            onChange={onChangeReminderTime}
            value={allDayEventReminderTime}
            testId={"start-time-picker"}
            dsVersion="2.0"
            showTimePicker={true}
            showIcon={false}
            timeFormat="hh:mm A"
          >
            <Button dsVersion="2.0" variant="neutral" type="outlined">
              <span className={classNames("text-body", classes.disclosure)}>
                {t("email:at")} {allDayEventReminderTime.format("hh:mm A")}
                <CaretDownFilled />
              </span>
            </Button>
          </TimePicker>
        </div>
      ) : null}
    </div>
  );
};

const ReminderContainer = props => {
  const {
    preferenceDropdownState,
    setPreferenceDropdownState,
    onAddReminder,
    setReminder,
    eventModalV2,
    showReminderDropdownRow = true,
    className,
    reminderRowContainerClass = "",
    reminderLabelClass = "",
    addReminderButtonClass = "",
    icon = <BellRingOutlined size={"xx-small"} />,
    setToastMsg,
    maxReminderLocalesKey = "calendar:max_reminder_reached",
  } = useReminderList(props);
  const { t } = useI18n(["calendar"]);
  const [showReminderDropdown, setShowReminderDropdown] = useState(
    showReminderDropdownRow
  );

  const showReminderDropdownRowsHandler = () => {
    setShowReminderDropdown(true);
  };

  // Custom hook to ensure element is in viewport

  const [remindAllBtnRef, scrollIntoViewOnClick] = useScrollIntoViewIfNeeded(
    showReminderDropdown
  );
  const addReminderHandler = params => {
    onAddReminder(params);
    scrollIntoViewOnClick();
  };

  return showReminderDropdown ? (
    <EventRow
      label="calendar:reminder"
      labelClass={classNames(classes.reminderLabel, reminderLabelClass)}
      className={classNames(
        classes.reminderRowContainer,
        reminderRowContainerClass
      )}
      icon={icon}
      renderComponent={() => (
        <div className={classNames(classes.reminderContainer, className)}>
          {_.map(
            preferenceDropdownState,
            (currPreferenceDropdown, reminderIndex) => {
              const removeReminder = () => {
                setPreferenceDropdownState(prev => {
                  const filteredReminders = _.filter(
                    prev,
                    (currReminder, currIndex) => {
                      return currIndex !== reminderIndex;
                    }
                  );
                  // set updated reminders to redux state
                  setReminder(filteredReminders);
                  return filteredReminders;
                });
              };

              const onRemindersChange = (value, id, key) => {
                const updatedState = _.cloneDeep(preferenceDropdownState);
                const targetPreference = _.find(
                  updatedState,
                  preference => preference.id === id
                );
                if (targetPreference) {
                  targetPreference[key] = value;
                  setReminder(
                    // Ensure scheduleTime is in the correct format which is string in redux state and object in local state
                    // so we need to convert it to string for redux state
                    // reminder?.scheduleTime?.value when user change the input local state have format {value, label}
                    _.map(updatedState, reminder => ({
                      ...reminder,
                      scheduleTime:
                        reminder?.scheduleTime?.value || reminder?.scheduleTime,
                    }))
                  );
                  setPreferenceDropdownState(updatedState);
                }
              };

              /**
               * Updates multiple properties of a reminder preference and synchronizes state.
               *
               * @param {Object} updates - An object containing key-value pairs of properties to update
               * @param {string|number} id - The unique identifier of the preference to update
               * @returns {void}
               *
               */
              const onRemindersChangeMultiple = (updates, id) => {
                const updatedState = _.cloneDeep(preferenceDropdownState);
                const targetPreference = _.find(
                  updatedState,
                  preference => preference.id === id
                );
                if (targetPreference) {
                  _.forEach(updates, (value, key) => {
                    targetPreference[key] = value;
                  });
                  setReminder(
                    _.map(updatedState, reminder => ({
                      ...reminder,
                      scheduleTime:
                        reminder?.scheduleTime?.value || reminder?.scheduleTime,
                    }))
                  );
                  setPreferenceDropdownState(updatedState);
                }
              };

              // Get existing reminder times excluding the current one
              const getExistingReminderTimes = currentId => {
                return _.chain(preferenceDropdownState)
                  .filter(reminder => reminder.id !== currentId)
                  .map(
                    reminder =>
                      reminder?.scheduleTime?.value || reminder?.scheduleTime
                  )
                  .filter(time => time && time !== "custom")
                  .value();
              };

              // Get existing reminders with full data for duplicate detection
              const getExistingRemindersData = currentId => {
                return _.chain(preferenceDropdownState)
                  .filter(reminder => reminder.id !== currentId)
                  .map(reminder => ({
                    scheduleTime:
                      reminder?.scheduleTime?.value || reminder?.scheduleTime,
                    triggerTimeOffset: reminder?.triggerTimeOffset,
                  }))
                  .filter(
                    data => data.scheduleTime && data.scheduleTime !== "custom"
                  )
                  .value();
              };

              return (
                <div
                  key={currPreferenceDropdown?.id}
                  className={classes.reminderItem}
                >
                  <Reminder
                    initialScheduleTime={currPreferenceDropdown?.scheduleTime}
                    sentViaState={currPreferenceDropdown?.sentVia}
                    preferenceId={currPreferenceDropdown?.id}
                    triggerDayOffset={currPreferenceDropdown?.triggerDayOffset}
                    triggerTimeOffset={
                      currPreferenceDropdown?.triggerTimeOffset
                    }
                    onRemindersChange={onRemindersChange}
                    onRemindersChangeMultiple={onRemindersChangeMultiple}
                    existingReminderTimes={getExistingReminderTimes(
                      currPreferenceDropdown?.id
                    )}
                    existingRemindersData={getExistingRemindersData(
                      currPreferenceDropdown?.id
                    )}
                    preferenceDropdownState={preferenceDropdownState}
                    serialNo={reminderIndex + 1}
                    isAllDayEvent={eventModalV2?.isAllDayEvent}
                    setToastMsg={setToastMsg}
                  />
                  <div>
                    <IconButton
                      icon={<MinusCircleOutlined />}
                      variant="neutral"
                      size="small"
                      onClick={removeReminder}
                      testId={`${props.testId}-reminder-button`}
                    />
                  </div>
                </div>
              );
            }
          )}
          {_.size(preferenceDropdownState) >= MAX_REMINDER_COUNT ? (
            <div
              ref={element => (remindAllBtnRef.current = element)}
              className={classNames("text-label", classes.maxReminder)}
            >
              {t(maxReminderLocalesKey)}
            </div>
          ) : (
            <Button
              variant="progressive"
              type="inline"
              onClick={addReminderHandler}
              className={classNames({
                [classes.addReminderButton]: true,
                [classes.addReminderButtonMargin]: _.isEmpty(
                  preferenceDropdownState
                ),
                [addReminderButtonClass]: true,
              })}
              testId={`${props.testId}-add-reminder-button`}
              icon={<AddCircleOutlined variant="success" />}
              size="medium"
              ref={element => (remindAllBtnRef.current = element)}
            >
              <span
                className={classNames("text-label", classes.addReminderText)}
              >
                {_.size(preferenceDropdownState) === 0
                  ? t("calendar:add_reminder")
                  : t("calendar:add_another_reminder")}
              </span>
            </Button>
          )}
        </div>
      )}
    />
  ) : (
    <Button
      variant="neutral"
      type="plain"
      onClick={showReminderDropdownRowsHandler}
      className={classes.reminderButton}
    >
      {t("calendar:remind_all_participants_msg")}
    </Button>
  );
};

const mapActionCreatorsLocal = {
  ...mapActionCreators,
  setToastMsg,
};

export default connect(
  mapStateToProps,
  mapActionCreatorsLocal
)(ReminderContainer);
